import React, { useEffect, useRef } from 'react';
import { AspectRatio } from '@mantine/core';
import { normalizeMediaSource } from './mediaSource';
import { getEmbedIframeSrc } from './embedProviders';
import { mediaFitVars, MEDIA_FIT } from './mediaFit';
import { useConsent } from '../consent/useConsent';
import { BlockedEmbedPlaceholder } from '../consent/BlockedEmbedPlaceholder';
import { useVideoReport } from '../tracking/videoTrackingContext';
import { attachEmbedPlayer } from '../tracking/videoPlayerBridge';
import classes from './MediaFrame.module.css';

export function MediaEmbed({
  media,
  playback: playbackOverride,
  mediaAspectRatio,
  ratio,
  mediaFit = 'cover',
  fill = false,
  title = 'Vidéo intégrée',
  isEditing = false,
  className = '',
  style,
}) {
  const { enforced, isServiceAllowed } = useConsent();
  const report = useVideoReport();
  const iframeRef = useRef(null);
  const resolved = normalizeMediaSource(media);
  const playback = {
    ...resolved.playback,
    ...(playbackOverride || {}),
    ...(isEditing ? { autoplay: false } : {}),
  };
  const iframeSrc = getEmbedIframeSrc(resolved.provider, resolved.src, playback);

  /* Le lecteur tiers ne dit rien de lui-même : on l'interroge, et seulement
     quand le suivi est actif. Hors site publié, `report.active` est faux et cet
     effet ne fait rien — aucun script tiers n'est chargé dans l'éditeur. */
  const { active, onPlay, onPause, onTime } = report;
  /* Même règle que pour le lecteur fichier : une vidéo qui démarre seule n'est
     pas un geste du lecteur, et la compter lui prêterait une intention. */
  const tracked = active && !playback.autoplay;
  useEffect(() => {
    if (!tracked || !iframeRef.current) return undefined;
    return attachEmbedPlayer(resolved.provider, iframeRef.current, { onPlay, onPause, onTime });
  }, [tracked, resolved.provider, iframeSrc, onPlay, onPause, onTime]);

  if (!iframeSrc) return null;

  const aspect = ratio ?? mediaAspectRatio ?? 16 / 9;
  const fitVars = mediaFitVars(mediaFit);
  const containFill = fill && mediaFit === MEDIA_FIT.contain;

  // L'un des deux seuls endroits du code qui montent un iframe — d'où la garde
  // ici plutôt que dans chaque bloc. Hors site publié `enforced` est faux, et
  // le rendu est exactement celui d'avant le chantier consentement.
  const blocked = enforced && !isServiceAllowed(resolved.provider);

  const frame = blocked ? (
    <BlockedEmbedPlaceholder serviceId={resolved.provider} />
  ) : (
    <iframe
      ref={(node) => { iframeRef.current = node; report.rootRef.current = node; }}
      src={iframeSrc}
      title={title}
      className={classes.embed}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      loading="lazy"
      style={{ pointerEvents: isEditing ? 'none' : 'auto' }}
    />
  );

  if (fill) {
    return (
      <div
        className={`${classes.embedFill} ${containFill ? classes.embedFillContain : ''} ${className}`.trim()}
        style={{ ...fitVars, ...style, '--media-aspect-ratio': aspect }}
      >
        {frame}
      </div>
    );
  }

  return (
    <div className={`${classes.frame} ${className}`.trim()} style={style}>
      <AspectRatio ratio={aspect}>
        {frame}
      </AspectRatio>
    </div>
  );
}
